import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill } from "../../components/common/Primitives";
import { Modal, ModalFooter } from "../../components/common/Modal";
import { Button } from "../../components/ui/button";
import api, { formatApiError } from "../../lib/api";
import { IndianRupee, Save, Ban, RotateCcw, Plus } from "lucide-react";

function formatINR(v) {
  const n = Number(v || 0);
  return `₹${n.toLocaleString("en-IN")}`;
}

export default function AdminDaasPricing() {
  const [modules, setModules] = useState([]);
  const [packages, setPackages] = useState([]);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({}); // { [type_id]: {...updated fields} }
  const [newModuleOpen, setNewModuleOpen] = useState(false);
  const [newPackageOpen, setNewPackageOpen] = useState(false);
  const [newModule, setNewModule] = useState({ module_id: "", name: "", description: "", price_inr: 0, list_price_inr: 0, duration_days: 7, category: "" });
  const [newPackage, setNewPackage] = useState({ package_id: "", name: "", description: "", modules: "", price_inr: 0, list_price_inr: 0, badge: "" });

  const load = async () => {
    try {
      const r = await api.get("/admin/daas/pricing");
      setModules(r.data.modules || []);
      setPackages(r.data.packages || []);
      setDrafts({});
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const draftFor = (type, id, base) => ({ ...base, ...(drafts[`${type}:${id}`] || {}) });
  const editField = (type, id, field, value) => {
    setDrafts((d) => ({ ...d, [`${type}:${id}`]: { ...(d[`${type}:${id}`] || {}), [field]: value } }));
  };

  const saveModule = async (module_id, current) => {
    setSavingKey(`m:${module_id}`);
    try {
      const payload = {
        name: current.name,
        description: current.description || "",
        price_inr: Number(current.price_inr) || 0,
        list_price_inr: current.list_price_inr === "" || current.list_price_inr == null
          ? null
          : Number(current.list_price_inr) || null,
        duration_days: Number(current.duration_days) || 0,
        category: current.category || "",
        active: current.active !== false,
      };
      if (payload.list_price_inr && payload.list_price_inr < payload.price_inr) {
        toast.error("List price must be >= discounted price");
        setSavingKey(null);
        return;
      }
      await api.put(`/admin/daas/pricing/modules/${module_id}`, payload);
      toast.success(`Module "${current.name}" saved`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingKey(null);
    }
  };

  const savePackage = async (package_id, current) => {
    setSavingKey(`p:${package_id}`);
    try {
      const payload = {
        name: current.name,
        description: current.description || "",
        modules: current.modules || [],
        price_inr: Number(current.price_inr) || 0,
        list_price_inr: Number(current.list_price_inr) || 0,
        savings_pct: Number(current.savings_pct) || 0,
        badge: current.badge || null,
        active: current.active !== false,
      };
      await api.put(`/admin/daas/pricing/packages/${package_id}`, payload);
      toast.success(`Package "${current.name}" saved`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingKey(null);
    }
  };

  const toggleActiveModule = async (module_id, current) => {
    await saveModule(module_id, { ...current, active: !current.active });
  };
  const toggleActivePackage = async (package_id, current) => {
    await savePackage(package_id, { ...current, active: !current.active });
  };

  const createNewModule = async () => {
    const mid = (newModule.module_id || "").trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!mid) return toast.error("Module ID is required");
    if (modules.some((m) => m.module_id === mid)) return toast.error("Module ID already exists");
    if (!newModule.name.trim()) return toast.error("Module name is required");
    setSavingKey(`m:${mid}:new`);
    try {
      await api.put(`/admin/daas/pricing/modules/${mid}`, {
        name: newModule.name.trim(),
        description: newModule.description || "",
        price_inr: Number(newModule.price_inr) || 0,
        list_price_inr: newModule.list_price_inr ? Number(newModule.list_price_inr) : null,
        duration_days: Number(newModule.duration_days) || 0,
        category: newModule.category || "",
        active: true,
      });
      toast.success(`Module "${newModule.name}" created`);
      setNewModule({ module_id: "", name: "", description: "", price_inr: 0, list_price_inr: 0, duration_days: 7, category: "" });
      setNewModuleOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingKey(null);
    }
  };

  const createNewPackage = async () => {
    const pid = (newPackage.package_id || "").trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!pid) return toast.error("Package ID is required");
    if (packages.some((p) => p.package_id === pid)) return toast.error("Package ID already exists");
    if (!newPackage.name.trim()) return toast.error("Package name is required");
    const moduleIds = (newPackage.modules || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (moduleIds.length === 0) return toast.error("Add at least one module");
    for (const mid of moduleIds) {
      if (!modules.some((m) => m.module_id === mid)) {
        return toast.error(`Unknown module: ${mid}`);
      }
    }
    if (Number(newPackage.list_price_inr) < Number(newPackage.price_inr)) {
      return toast.error("List price must be >= price");
    }
    setSavingKey(`p:${pid}:new`);
    try {
      await api.put(`/admin/daas/pricing/packages/${pid}`, {
        name: newPackage.name.trim(),
        description: newPackage.description || "",
        modules: moduleIds,
        price_inr: Number(newPackage.price_inr) || 0,
        list_price_inr: Number(newPackage.list_price_inr) || 0,
        savings_pct: 0, // auto-computed
        badge: newPackage.badge || null,
        active: true,
      });
      toast.success(`Package "${newPackage.name}" created`);
      setNewPackage({ package_id: "", name: "", description: "", modules: "", price_inr: 0, list_price_inr: 0, badge: "" });
      setNewPackageOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <DashboardShell title="DaaS Pricing" subtitle="ADMIN · CATALOG PRICING">
      <Toaster richColors position="top-right" />

      <div className="text-sm text-ink-soft mb-4 flex items-center gap-2">
        <IndianRupee size={14} className="text-gold" />
        Update per-module and per-package pricing. Changes take effect immediately across the DaaS catalog, quote engine, and founder store.
      </div>

      <Card testId="daas-pricing-modules">
        <div className="flex items-center justify-between mb-4">
          <div className="overline">Diligence Modules ({modules.length})</div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => setNewModuleOpen(true)}
              data-testid="daas-pricing-new-module-btn"
            >
              <Plus size={12} className="mr-1" /> New Module
            </Button>
            <button
              onClick={load}
              className="text-xs font-mono text-ink-muted hover:text-gold flex items-center gap-1"
              data-testid="daas-pricing-refresh-modules"
            >
              <RotateCcw size={12} /> Refresh
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="daas-pricing-modules-table">
            <thead>
              <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-ink-muted border-b border-white/10">
                <th className="py-2 pr-3">ID</th>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Price ₹</th>
                <th className="py-2 pr-3">List ₹ <span className="normal-case text-[9px] text-ink-muted/70">(struck-off)</span></th>
                <th className="py-2 pr-3">Duration (d)</th>
                <th className="py-2 pr-3">Category</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => {
                const cur = draftFor("m", m.module_id, m);
                const dirty = Boolean(drafts[`m:${m.module_id}`]);
                return (
                  <tr key={m.module_id} className="border-b border-white/5" data-testid={`daas-pricing-module-row-${m.module_id}`}>
                    <td className="py-2 pr-3 font-mono text-[11px] text-ink-muted">{m.module_id}</td>
                    <td className="py-2 pr-3">
                      <input
                        className="bg-transparent border border-white/10 focus:border-gold rounded px-2 py-1 text-sm w-48"
                        value={cur.name || ""}
                        onChange={(e) => editField("m", m.module_id, "name", e.target.value)}
                        data-testid={`daas-pricing-module-name-${m.module_id}`}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        min={0}
                        className="bg-transparent border border-white/10 focus:border-gold rounded px-2 py-1 text-sm w-28 font-mono"
                        value={cur.price_inr ?? 0}
                        onChange={(e) => editField("m", m.module_id, "price_inr", e.target.value)}
                        data-testid={`daas-pricing-module-price-${m.module_id}`}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        min={0}
                        placeholder="—"
                        className="bg-transparent border border-white/10 focus:border-gold rounded px-2 py-1 text-sm w-28 font-mono"
                        value={cur.list_price_inr ?? ""}
                        onChange={(e) => editField("m", m.module_id, "list_price_inr", e.target.value)}
                        data-testid={`daas-pricing-module-listprice-${m.module_id}`}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        min={0}
                        className="bg-transparent border border-white/10 focus:border-gold rounded px-2 py-1 text-sm w-20 font-mono"
                        value={cur.duration_days ?? 0}
                        onChange={(e) => editField("m", m.module_id, "duration_days", e.target.value)}
                        data-testid={`daas-pricing-module-duration-${m.module_id}`}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        className="bg-transparent border border-white/10 focus:border-gold rounded px-2 py-1 text-sm w-32"
                        value={cur.category || ""}
                        onChange={(e) => editField("m", m.module_id, "category", e.target.value)}
                        data-testid={`daas-pricing-module-category-${m.module_id}`}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <Pill tone={cur.active !== false ? "success" : "muted"}>
                        {cur.active !== false ? "Active" : "Inactive"}
                      </Pill>
                    </td>
                    <td className="py-2 pr-3 flex items-center gap-2">
                      <Button
                        size="sm"
                        disabled={!dirty || savingKey === `m:${m.module_id}`}
                        onClick={() => saveModule(m.module_id, cur)}
                        data-testid={`daas-pricing-module-save-${m.module_id}`}
                      >
                        <Save size={12} className="mr-1" /> Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleActiveModule(m.module_id, cur)}
                        data-testid={`daas-pricing-module-toggle-${m.module_id}`}
                      >
                        <Ban size={12} className="mr-1" />
                        {cur.active !== false ? "Deactivate" : "Reactivate"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="h-4" />

      <Card testId="daas-pricing-packages">
        <div className="flex items-center justify-between mb-4">
          <div className="overline">Diligence Packages ({packages.length})</div>
          <Button
            size="sm"
            onClick={() => setNewPackageOpen(true)}
            data-testid="daas-pricing-new-package-btn"
          >
            <Plus size={12} className="mr-1" /> New Package
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="daas-pricing-packages-table">
            <thead>
              <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-ink-muted border-b border-white/10">
                <th className="py-2 pr-3">ID</th>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Modules</th>
                <th className="py-2 pr-3">Price ₹</th>
                <th className="py-2 pr-3">List ₹</th>
                <th className="py-2 pr-3">Badge</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((p) => {
                const cur = draftFor("p", p.package_id, p);
                const dirty = Boolean(drafts[`p:${p.package_id}`]);
                return (
                  <tr key={p.package_id} className="border-b border-white/5" data-testid={`daas-pricing-package-row-${p.package_id}`}>
                    <td className="py-2 pr-3 font-mono text-[11px] text-ink-muted">{p.package_id}</td>
                    <td className="py-2 pr-3">
                      <input
                        className="bg-transparent border border-white/10 focus:border-gold rounded px-2 py-1 text-sm w-52"
                        value={cur.name || ""}
                        onChange={(e) => editField("p", p.package_id, "name", e.target.value)}
                        data-testid={`daas-pricing-package-name-${p.package_id}`}
                      />
                    </td>
                    <td className="py-2 pr-3 text-[11px] font-mono text-ink-muted">
                      {(cur.modules || []).join(", ")}
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        min={0}
                        className="bg-transparent border border-white/10 focus:border-gold rounded px-2 py-1 text-sm w-28 font-mono"
                        value={cur.price_inr ?? 0}
                        onChange={(e) => editField("p", p.package_id, "price_inr", e.target.value)}
                        data-testid={`daas-pricing-package-price-${p.package_id}`}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        min={0}
                        className="bg-transparent border border-white/10 focus:border-gold rounded px-2 py-1 text-sm w-28 font-mono"
                        value={cur.list_price_inr ?? 0}
                        onChange={(e) => editField("p", p.package_id, "list_price_inr", e.target.value)}
                        data-testid={`daas-pricing-package-list-${p.package_id}`}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        className="bg-transparent border border-white/10 focus:border-gold rounded px-2 py-1 text-sm w-32"
                        value={cur.badge || ""}
                        onChange={(e) => editField("p", p.package_id, "badge", e.target.value)}
                        placeholder="—"
                        data-testid={`daas-pricing-package-badge-${p.package_id}`}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <Pill tone={cur.active !== false ? "success" : "muted"}>
                        {cur.active !== false ? "Active" : "Inactive"}
                      </Pill>
                    </td>
                    <td className="py-2 pr-3 flex items-center gap-2">
                      <Button
                        size="sm"
                        disabled={!dirty || savingKey === `p:${p.package_id}`}
                        onClick={() => savePackage(p.package_id, cur)}
                        data-testid={`daas-pricing-package-save-${p.package_id}`}
                      >
                        <Save size={12} className="mr-1" /> Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleActivePackage(p.package_id, cur)}
                        data-testid={`daas-pricing-package-toggle-${p.package_id}`}
                      >
                        <Ban size={12} className="mr-1" />
                        {cur.active !== false ? "Deactivate" : "Reactivate"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-3 text-[11px] font-mono text-ink-muted">
          Tip: package savings % auto-recomputes from list vs price when saved.
        </div>
      </Card>

      <div className="text-xs text-ink-muted mt-4">
        Live catalog preview: <span className="font-mono">{modules.filter((m) => m.active !== false).length}</span> active modules, <span className="font-mono">{packages.filter((p) => p.active !== false).length}</span> active packages — total unbundled cost <span className="font-mono">{formatINR(modules.filter((m) => m.active !== false).reduce((s, m) => s + Number(m.price_inr || 0), 0))}</span>.
      </div>

      <Modal
        open={newModuleOpen}
        onClose={() => setNewModuleOpen(false)}
        title="Create Diligence Module"
        subtitle="Launch a new paid diligence product without a code push."
        size="md"
        testId="new-module-modal"
      >
        <div className="space-y-3 text-sm">
          <label className="block">
            <div className="overline mb-1">Module ID</div>
            <input
              className="w-full border border-navy/15 rounded px-3 py-2 text-sm font-mono lowercase"
              placeholder="e.g. hr_compliance_audit"
              value={newModule.module_id}
              onChange={(e) => setNewModule((s) => ({ ...s, module_id: e.target.value }))}
              data-testid="new-module-id-input"
            />
            <div className="text-[10px] text-ink-muted mt-1 font-mono">Lowercase, underscores only. Cannot be changed later.</div>
          </label>
          <label className="block">
            <div className="overline mb-1">Name</div>
            <input
              className="w-full border border-navy/15 rounded px-3 py-2 text-sm"
              value={newModule.name}
              onChange={(e) => setNewModule((s) => ({ ...s, name: e.target.value }))}
              data-testid="new-module-name-input"
            />
          </label>
          <label className="block">
            <div className="overline mb-1">Description</div>
            <textarea
              className="w-full border border-navy/15 rounded px-3 py-2 text-sm h-20"
              value={newModule.description}
              onChange={(e) => setNewModule((s) => ({ ...s, description: e.target.value }))}
              data-testid="new-module-description-input"
            />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label>
              <div className="overline mb-1">Price ₹</div>
              <input
                type="number"
                min={0}
                className="w-full border border-navy/15 rounded px-3 py-2 text-sm font-mono"
                value={newModule.price_inr}
                onChange={(e) => setNewModule((s) => ({ ...s, price_inr: e.target.value }))}
                data-testid="new-module-price-input"
              />
            </label>
            <label>
              <div className="overline mb-1">List ₹ (optional)</div>
              <input
                type="number"
                min={0}
                placeholder="—"
                className="w-full border border-navy/15 rounded px-3 py-2 text-sm font-mono"
                value={newModule.list_price_inr}
                onChange={(e) => setNewModule((s) => ({ ...s, list_price_inr: e.target.value }))}
                data-testid="new-module-listprice-input"
              />
              <div className="text-[10px] text-ink-muted mt-1 font-mono">Struck-off original price. Leave blank for none.</div>
            </label>
            <label>
              <div className="overline mb-1">Duration (d)</div>
              <input
                type="number"
                min={0}
                className="w-full border border-navy/15 rounded px-3 py-2 text-sm font-mono"
                value={newModule.duration_days}
                onChange={(e) => setNewModule((s) => ({ ...s, duration_days: e.target.value }))}
                data-testid="new-module-duration-input"
              />
            </label>
            <label className="col-span-3">
              <div className="overline mb-1">Category</div>
              <input
                className="w-full border border-navy/15 rounded px-3 py-2 text-sm"
                placeholder="e.g. Compliance"
                value={newModule.category}
                onChange={(e) => setNewModule((s) => ({ ...s, category: e.target.value }))}
                data-testid="new-module-category-input"
              />
            </label>
          </div>
        </div>
        <ModalFooter>
          <Button variant="outline" size="sm" onClick={() => setNewModuleOpen(false)} data-testid="new-module-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={createNewModule} disabled={savingKey?.endsWith(":new")} data-testid="new-module-create-btn">
            <Plus size={12} className="mr-1" /> Create Module
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        open={newPackageOpen}
        onClose={() => setNewPackageOpen(false)}
        title="Create Diligence Package"
        subtitle="Bundle existing modules at a discount."
        size="md"
        testId="new-package-modal"
      >
        <div className="space-y-3 text-sm">
          <label className="block">
            <div className="overline mb-1">Package ID</div>
            <input
              className="w-full border border-navy/15 rounded px-3 py-2 text-sm font-mono lowercase"
              placeholder="e.g. essentials_pack"
              value={newPackage.package_id}
              onChange={(e) => setNewPackage((s) => ({ ...s, package_id: e.target.value }))}
              data-testid="new-package-id-input"
            />
          </label>
          <label className="block">
            <div className="overline mb-1">Name</div>
            <input
              className="w-full border border-navy/15 rounded px-3 py-2 text-sm"
              value={newPackage.name}
              onChange={(e) => setNewPackage((s) => ({ ...s, name: e.target.value }))}
              data-testid="new-package-name-input"
            />
          </label>
          <label className="block">
            <div className="overline mb-1">Description</div>
            <textarea
              className="w-full border border-navy/15 rounded px-3 py-2 text-sm h-16"
              value={newPackage.description}
              onChange={(e) => setNewPackage((s) => ({ ...s, description: e.target.value }))}
              data-testid="new-package-description-input"
            />
          </label>
          <label className="block">
            <div className="overline mb-1">Modules (comma-separated IDs)</div>
            <input
              className="w-full border border-navy/15 rounded px-3 py-2 text-sm font-mono"
              placeholder="pitch_review, deep_financial_review"
              value={newPackage.modules}
              onChange={(e) => setNewPackage((s) => ({ ...s, modules: e.target.value }))}
              data-testid="new-package-modules-input"
            />
            <div className="text-[10px] text-ink-muted mt-1 font-mono">
              Available: {modules.map((m) => m.module_id).join(", ")}
            </div>
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label>
              <div className="overline mb-1">Price ₹</div>
              <input
                type="number"
                min={0}
                className="w-full border border-navy/15 rounded px-3 py-2 text-sm font-mono"
                value={newPackage.price_inr}
                onChange={(e) => setNewPackage((s) => ({ ...s, price_inr: e.target.value }))}
                data-testid="new-package-price-input"
              />
            </label>
            <label>
              <div className="overline mb-1">List ₹</div>
              <input
                type="number"
                min={0}
                className="w-full border border-navy/15 rounded px-3 py-2 text-sm font-mono"
                value={newPackage.list_price_inr}
                onChange={(e) => setNewPackage((s) => ({ ...s, list_price_inr: e.target.value }))}
                data-testid="new-package-list-input"
              />
            </label>
            <label>
              <div className="overline mb-1">Badge</div>
              <input
                className="w-full border border-navy/15 rounded px-3 py-2 text-sm"
                placeholder="Most Popular"
                value={newPackage.badge}
                onChange={(e) => setNewPackage((s) => ({ ...s, badge: e.target.value }))}
                data-testid="new-package-badge-input"
              />
            </label>
          </div>
        </div>
        <ModalFooter>
          <Button variant="outline" size="sm" onClick={() => setNewPackageOpen(false)} data-testid="new-package-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={createNewPackage} disabled={savingKey?.endsWith(":new")} data-testid="new-package-create-btn">
            <Plus size={12} className="mr-1" /> Create Package
          </Button>
        </ModalFooter>
      </Modal>
    </DashboardShell>
  );
}
